import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { StudioMembership } from "../../helpers/identity_storage";
import { getMemberships } from "../../services/authService";

export const OPEN_STUDIO_SWITCHER_EVENT = "adatis:studio-switcher:open";
export const MEMBERSHIPS_CHANGED_EVENT = "adatis:memberships:changed";
export const MEMBERSHIP_INVITE_SEEN_KEY = "adatis.membership.pendingSeen";

export type OpenStudioSwitcherDetail = {
  membershipId?: number | null;
};

const POLL_MS = 60_000;

const readSeenPendingIds = (): number[] => {
  try {
    const raw = sessionStorage.getItem(MEMBERSHIP_INVITE_SEEN_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map(Number).filter((value) => Number.isFinite(value));
  } catch {
    return [];
  }
};

const writeSeenPendingIds = (ids: number[]): void => {
  try {
    sessionStorage.setItem(MEMBERSHIP_INVITE_SEEN_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
};

export const dispatchOpenStudioSwitcher = (
  detail?: OpenStudioSwitcherDetail
): void => {
  window.dispatchEvent(
    new CustomEvent<OpenStudioSwitcherDetail>(OPEN_STUDIO_SWITCHER_EVENT, {
      detail: detail ?? {},
    })
  );
};

export const dispatchMembershipsChanged = (): void => {
  window.dispatchEvent(new CustomEvent(MEMBERSHIPS_CHANGED_EVENT));
};

const pendingMemberships = (memberships: StudioMembership[]): StudioMembership[] =>
  memberships.filter((item) => item.status === "pending" && item.id != null);

/**
 * Detects new pending studio invites while the user is already inside a tenant
 * (poll + focus) and surfaces a toast that opens the studio switcher.
 */
const MembershipInviteWatcher = () => {
  const { t } = useTranslation();
  const bootstrappedRef = useRef(false);

  const syncPending = useCallback(async () => {
    try {
      const response = await getMemberships();

      if (!response.success || !response.data) {
        return;
      }

      const pending = pendingMemberships(response.data.memberships || []);
      const pendingIds = pending
        .map((item) => item.id)
        .filter((id): id is number => typeof id === "number");

      const seen = readSeenPendingIds();

      if (!bootstrappedRef.current) {
        bootstrappedRef.current = true;
        writeSeenPendingIds(pendingIds);
        return;
      }

      const newly = pending.filter(
        (item) => item.id != null && !seen.includes(item.id)
      );

      writeSeenPendingIds(pendingIds);

      if (newly.length === 0) {
        return;
      }

      const first = newly[0];
      const studioName =
        first.tenant_name || t("auth.select_studio.unnamed");

      toast.info(
        newly.length === 1
          ? t("auth.invite_watch.toast_one", { name: studioName })
          : t("auth.invite_watch.toast_many", { count: newly.length }),
        {
          onClick: () => dispatchOpenStudioSwitcher(),
          autoClose: 12000,
        }
      );

      window.dispatchEvent(new CustomEvent("adatis:notifications:refresh"));
      dispatchMembershipsChanged();
    } catch {
      // Silent: invite mail + bell notification remain the primary channels.
    }
  }, [t]);

  useEffect(() => {
    void syncPending();

    const intervalId = window.setInterval(() => {
      void syncPending();
    }, POLL_MS);

    const onFocus = () => {
      void syncPending();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        onFocus();
      }
    });

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [syncPending]);

  return null;
};

export default MembershipInviteWatcher;
